import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import ResendIcon from '@mui/icons-material/Send';
import CancelInviteIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';
import {
  organizationService,
  OrganizationMember,
  Invitation,
  Role,
} from '@services/organizationService';
import { useAuthStore } from '@store/index';
import InviteMemberDialog from './InviteMemberDialog';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { formatDate } from '@utils/format';
import { toDisplayError } from '@utils/apiError';

interface MembersTabProps {
  orgId: string;
}

interface ActionMenuState {
  anchorEl: HTMLElement | null;
  member: OrganizationMember | null;
}

interface RoleMenuState {
  anchorEl: HTMLElement | null;
  member: OrganizationMember | null;
}

function MembersTab({ orgId }: MembersTabProps) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [invitationActionId, setInvitationActionId] = useState<string | null>(null);
  // Resend/cancel feedback so an invitation action gives visible confirmation
  // (the deleted Invitations tab did this; REV-488 review restored it).
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Action menu state
  const [actionMenu, setActionMenu] = useState<ActionMenuState>({
    anchorEl: null,
    member: null,
  });

  // Role menu state
  const [roleMenu, setRoleMenu] = useState<RoleMenuState>({
    anchorEl: null,
    member: null,
  });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'remove' | 'deactivate' | 'transfer' | null;
    member: OrganizationMember | null;
  }>({ open: false, type: null, member: null });
  const [processing, setProcessing] = useState(false);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await organizationService.getMembers(orgId, {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      });
      setMembers(result.items ?? []);
      setTotal(result.total);
    } catch (err) {
      setError(toDisplayError(err, t('organizations.members.loadFailed')));
      console.error('Failed to fetch members:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, paginationModel, t]);

  const fetchInvitations = useCallback(async () => {
    try {
      const result = await organizationService.getInvitations(orgId, { limit: 100 });
      setInvitations(result.items.filter((inv) => inv.status === 'pending'));
    } catch (err) {
      // Pending invitations are supplementary; don't fail the whole list.
      console.error('Failed to fetch invitations:', err);
    }
  }, [orgId]);

  const fetchRoles = useCallback(async () => {
    try {
      const result = await organizationService.getRoles(orgId);
      setRoles(result);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  }, [orgId]);

  useEffect(() => {
    void fetchMembers();
    void fetchInvitations();
    void fetchRoles();
  }, [fetchMembers, fetchInvitations, fetchRoles]);

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, member: OrganizationMember) => {
    setActionMenu({ anchorEl: event.currentTarget, member });
  };

  const handleActionClose = () => {
    setActionMenu({ anchorEl: null, member: null });
  };

  const handleRoleClick = (event: React.MouseEvent<HTMLElement>, member: OrganizationMember) => {
    setRoleMenu({ anchorEl: event.currentTarget, member });
  };

  const handleRoleClose = () => {
    setRoleMenu({ anchorEl: null, member: null });
  };

  const handleRoleChange = async (role: string) => {
    if (!roleMenu.member) return;

    setProcessing(true);
    setFeedback(null);
    try {
      await organizationService.assignRole(orgId, roleMenu.member.user_id, role);
      await fetchMembers();
    } catch (err) {
      console.error('Failed to change role:', err);
      // REV-967: say why. The owner role changes only by transfer, and a page
      // that is out of date can still send a change the API refuses.
      setFeedback({
        severity: 'error',
        message: toDisplayError(
          err,
          t('organizations.members.roleChangeFailed', 'Could not change the role.')
        ),
      });
      // REV-1166: reload, so an out-of-date page stops offering the change.
      void fetchMembers();
    } finally {
      setProcessing(false);
      handleRoleClose();
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    setInvitationActionId(invitation.id);
    setFeedback(null);
    try {
      await organizationService.resendInvitation(orgId, invitation.id);
      setFeedback({
        severity: 'success',
        message: t('organizations.invitations.resendSuccess', {
          defaultValue: 'Invitation resent to {{email}}.',
          email: invitation.email,
        }),
      });
    } catch (err) {
      console.error('Failed to resend invitation:', err);
      setFeedback({
        severity: 'error',
        message: t(
          'organizations.invitations.resendFailed',
          'Could not resend the invitation. Please try again.'
        ),
      });
    } finally {
      setInvitationActionId(null);
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    setInvitationActionId(invitation.id);
    setFeedback(null);
    try {
      await organizationService.cancelInvitation(orgId, invitation.id);
      await fetchInvitations();
      setFeedback({
        severity: 'success',
        message: t('organizations.invitations.cancelSuccess', {
          defaultValue: 'Invitation to {{email}} cancelled.',
          email: invitation.email,
        }),
      });
    } catch (err) {
      console.error('Failed to cancel invitation:', err);
      setFeedback({
        severity: 'error',
        message: t('organizations.invitations.cancelFailed', 'Could not cancel the invitation.'),
      });
    } finally {
      setInvitationActionId(null);
    }
  };

  /** What to say when an action fails and the API's code says nothing more specific. */
  const actionFailedMessage = (type: typeof confirmDialog.type, reactivating: boolean) => {
    switch (type) {
      case 'remove':
        return t('organizations.members.removeFailed', 'Could not remove the member.');
      case 'deactivate':
        return reactivating
          ? t('organizations.members.reactivateFailed', 'Could not reactivate the member.')
          : t('organizations.members.deactivateFailed', 'Could not deactivate the member.');
      case 'transfer':
      default:
        return t('organizations.members.transferFailed', 'Could not transfer ownership.');
    }
  };

  const handleConfirmAction = async () => {
    const { type, member } = confirmDialog;
    if (!member) return;
    const reactivating = type === 'deactivate' && member.status === 'suspended';

    setProcessing(true);
    setFeedback(null);
    try {
      switch (type) {
        case 'remove':
          await organizationService.removeMember(orgId, member.user_id);
          break;
        case 'deactivate':
          if (reactivating) {
            await organizationService.reactivateMember(orgId, member.user_id);
          } else {
            await organizationService.deactivateMember(orgId, member.user_id);
          }
          break;
        case 'transfer':
          await organizationService.transferOwnership(orgId, member.user_id);
          break;
      }
      await fetchMembers();
    } catch (err) {
      console.error('Failed to perform action:', err);
      // REV-1166: say why, as handleRoleChange does (REV-967). A catalogued
      // code shows its own text; a bare status falls back to the action's.
      setFeedback({
        severity: 'error',
        message: toDisplayError(err, actionFailedMessage(type, reactivating)),
      });
      // A refusal usually means the page is out of date (someone else already
      // acted). Reload, so it stops offering what the server refuses.
      void fetchMembers();
    } finally {
      setProcessing(false);
      setConfirmDialog({ open: false, type: null, member: null });
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'manager':
        return 'info';
      case 'member':
        return 'primary';
      default:
        return 'default';
    }
  };

  // REV-883: the confirmation names the action it performs. It used to say
  // "Save" for every one, and asked "deactivate this member?" when
  // reactivating a suspended one. The same label heads the dialog and sits on
  // its confirm button.
  const isReactivate =
    confirmDialog.type === 'deactivate' && confirmDialog.member?.status === 'suspended';
  const confirmCopy = (() => {
    switch (confirmDialog.type) {
      case 'remove':
        return {
          action: t('organizations.members.remove'),
          message: t('organizations.members.confirmRemove'),
        };
      case 'deactivate':
        return isReactivate
          ? {
              action: t('organizations.members.reactivate'),
              message: t('organizations.members.confirmReactivate'),
            }
          : {
              action: t('organizations.members.deactivate'),
              message: t('organizations.members.confirmDeactivate'),
            };
      case 'transfer':
        return {
          action: t('organizations.members.transferOwnership'),
          message: t('organizations.members.confirmTransfer'),
        };
      default:
        return { action: '', message: '' };
    }
  })();

  const columns: GridColDef[] = [
    {
      field: 'user',
      headerName: t('organizations.members.title'),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<OrganizationMember>) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.user.first_name} {params.row.user.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.user.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: t('organizations.members.role'),
      width: 150,
      renderCell: (params: GridRenderCellParams<OrganizationMember>) => {
        const canChangeRole = params.row.role !== 'owner' && params.row.user_id !== currentUser?.id;
        const roleLabel = t(`organizations.roles.${params.row.role}`, params.row.role);
        if (!canChangeRole) {
          return <Chip label={roleLabel} size="small" color={getRoleColor(params.row.role)} />;
        }
        // REV-917: it opens a menu, so it is a real <button> that says so. A
        // Chip given only onClick rendered a div with role="button" (CLAUDE.md,
        // Links vs buttons) and never said it opened a menu or whether it was
        // open. The name keeps the visible role in it (WCAG 2.5.3).
        const isOpen =
          Boolean(roleMenu.anchorEl) && roleMenu.member?.user_id === params.row.user_id;
        const name =
          [params.row.user?.first_name, params.row.user?.last_name].filter(Boolean).join(' ') ||
          params.row.user?.email;
        return (
          <Chip
            component="button"
            type="button"
            label={roleLabel}
            size="small"
            color={getRoleColor(params.row.role)}
            onClick={(e: React.MouseEvent<HTMLElement>) => handleRoleClick(e, params.row)}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={t('organizations.members.changeRoleFor', {
              defaultValue: 'Change role for {{name}}, currently {{role}}',
              name,
              role: roleLabel,
            })}
          />
        );
      },
    },
    {
      field: 'status',
      headerName: t('organizations.members.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams<OrganizationMember>) => (
        <Chip
          label={t(`organizations.members.statuses.${params.row.status}`)}
          size="small"
          color={params.row.status === 'active' ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'joined_at',
      headerName: t('organizations.members.joinedAt'),
      width: 150,
      // REV-882: never "Invalid Date". The API sends joined_at for every
      // member now, but a missing or unparseable value renders as a dash.
      valueFormatter: (value?: string) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: t('organizations.members.actions'),
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams<OrganizationMember>) => {
        // The organization owner and your own membership have no available
        // actions. Rather than hide the control (which leaves an unexplained
        // blank cell), keep it in every row and explain why it does nothing
        // (REV-450).
        //
        // REV-884: aria-disabled, never `disabled` (CLAUDE.md, Links vs
        // Buttons). `disabled` removed the control from the tab order, so the
        // explanation never reached a keyboard or screen-reader user. The
        // control keeps the same name as its working siblings ("Actions"), and
        // the reason sits outside it, referenced by aria-describedby, the same
        // pattern as NavigationDrawer's unavailable items.
        const isOwner = params.row.role === 'owner';
        const isSelf = params.row.user_id === currentUser?.id;
        if (isOwner || isSelf) {
          const reason = isOwner
            ? t('organizations.members.noActionsOwner')
            : t('organizations.members.noActionsSelf');
          const whyId = `member-${params.row.user_id}-why-no-actions`;
          return (
            <>
              <Tooltip title={reason}>
                <IconButton
                  aria-label={t('organizations.members.actions')}
                  aria-disabled="true"
                  aria-describedby={whyId}
                  onClick={(e) => e.preventDefault()}
                  sx={{ color: 'action.disabled', cursor: 'default' }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Box component="span" id={whyId} sx={visuallyHidden}>
                {reason}
              </Box>
            </>
          );
        }

        return (
          <IconButton
            onClick={(e) => handleActionClick(e, params.row)}
            aria-label={t('organizations.members.actions')}
          >
            <MoreVertIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('organizations.members.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteOpen(true)}
        >
          {t('organizations.members.invite')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
          role="status"
        >
          {feedback.message}
        </Alert>
      )}

      {/* REV-488: pending invitations are shown in their own section above the
          (server-paginated) members grid. Folding them into the grid's rows
          broke pagination — the same invites re-appeared on every page and the
          row count was wrong — so they live in their own small table here. */}
      {invitations.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ px: 2, pt: 2 }}>
            {t('organizations.members.pendingInvitations', 'Pending invitations')}
          </Typography>
          <ScrollableTableContainer>
            <Table
              size="small"
              aria-label={t('organizations.members.pendingInvitations', 'Pending invitations')}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('organizations.members.title')}</TableCell>
                  <TableCell>{t('organizations.members.role')}</TableCell>
                  <TableCell>{t('organizations.members.status')}</TableCell>
                  <TableCell align="right">{t('organizations.members.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map((inv) => {
                  const busy = invitationActionId === inv.id;
                  const resendLabel = t('organizations.invitations.resendFor', {
                    defaultValue: 'Resend invitation to {{email}}',
                    email: inv.email,
                  });
                  const cancelLabel = t('organizations.invitations.cancelFor', {
                    defaultValue: 'Cancel invitation to {{email}}',
                    email: inv.email,
                  });
                  return (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.email}</TableCell>
                      <TableCell>
                        {/* role is an object here, not a slug — interpolating it
                            produced the key `organizations.roles.[object Object]`
                            and rendered it raw (REV-524). */}
                        {inv.role
                          ? // Translate like the members grid does; fall back to
                            // the API's name so custom org roles (which have no
                            // i18n key) still render.
                            t(`organizations.roles.${inv.role.slug}`, inv.role.name)
                          : t('organizations.members.unknownRole', 'Unknown role')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t('organizations.members.pending', 'Pending')}
                          size="small"
                          color="warning"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={resendLabel}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleResendInvitation(inv)}
                              disabled={busy}
                              aria-label={resendLabel}
                            >
                              {busy ? (
                                <CircularProgress size={18} />
                              ) : (
                                <ResendIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={cancelLabel}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleCancelInvitation(inv)}
                              disabled={busy}
                              aria-label={cancelLabel}
                            >
                              <CancelInviteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        </Paper>
      )}

      <DataGrid
        rows={members}
        columns={columns}
        rowCount={total}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        disableRowSelectionOnClick
        autoHeight
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 1,
          },
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={handleActionClose}
      >
        {actionMenu.member?.role !== 'owner' && (
          <>
            <MenuItem
              onClick={() => {
                setConfirmDialog({
                  open: true,
                  type: 'deactivate',
                  member: actionMenu.member,
                });
                handleActionClose();
              }}
            >
              <ListItemIcon>
                <PersonOffIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {actionMenu.member?.status === 'suspended'
                  ? t('organizations.members.reactivate')
                  : t('organizations.members.deactivate')}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setConfirmDialog({
                  open: true,
                  type: 'remove',
                  member: actionMenu.member,
                });
                handleActionClose();
              }}
            >
              <ListItemIcon>
                <PersonRemoveIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>
                {t('organizations.members.remove')}
              </ListItemText>
            </MenuItem>
          </>
        )}
        {actionMenu.member?.role !== 'owner' && (
          <MenuItem
            onClick={() => {
              setConfirmDialog({
                open: true,
                type: 'transfer',
                member: actionMenu.member,
              });
              handleActionClose();
            }}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('organizations.members.transferOwnership')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Role Menu */}
      <Menu
        anchorEl={roleMenu.anchorEl}
        open={Boolean(roleMenu.anchorEl)}
        onClose={handleRoleClose}
      >
        {(roles || [])
          .filter((role) => role.slug !== 'owner')
          .map((role) => (
            <MenuItem
              key={role.slug}
              onClick={() => handleRoleChange(role.slug)}
              selected={roleMenu.member?.role === role.slug}
              disabled={processing}
            >
              <ListItemIcon>
                <AdminIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={role.name} secondary={role.description} />
            </MenuItem>
          ))}
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !processing && setConfirmDialog({ open: false, type: null, member: null })}
      >
        <DialogTitle>{confirmCopy.action}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmCopy.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, type: null, member: null })}
            disabled={processing}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.type === 'remove' ? 'error' : 'primary'}
            onClick={handleConfirmAction}
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} color="inherit" /> : confirmCopy.action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite member — opens in place. A successful invite adds a pending
          invitation row to this list (REV-488). */}
      <InviteMemberDialog
        open={inviteOpen}
        orgId={orgId}
        onClose={() => setInviteOpen(false)}
        onInvited={fetchInvitations}
      />
    </Box>
  );
}

export default MembersTab;
